import { DefaultPropertyInspector } from './walker';
import { IAttribute, IModel, IViolation } from './annotation';
import { Metamodel } from 'app/model/base/model';
import { Owned, Named } from './named';
import { RequiredValidator } from './validators';
import { Validators } from './all-validators';
import { ExecuteSSISPackageActivity } from '../pipeline/ssis.activity';
import { ExpressionResolverService } from 'app/services/expression-resolver-service';
import { buildExpressionErrorMessage } from '../expressions/expression-utils';
import { LoggerService } from 'app/logger/logger.service';
import { of, interval } from 'rxjs';
import { convertObjectToString } from 'app/shared/utils/string.util';
import { Dataset } from '../dataset/dataset';
import { Pipeline } from '../pipeline/pipeline';
import { Activity } from '../pipeline/activity';
import { LinkedService } from '../linkedservice/linkedservice';
import { Trigger } from '../trigger/trigger';
import { AzureKeyVault } from '../linkedservice/key-vault';
import { sleep } from 'app/shared/utils/sleep.util';
import { GlobalVariables, ResourceType, subResourceTypeMap } from 'app/services/globals';
import {
    IOwnedViolation,
    IValidationContext,
    ModelMarker,
    ValidateInternalObject,
    Table,
    Database,
    SqlPool,
    ModelConstants,
    ValidationErrorType,
    GlobalVariables as ShellGlobalVariables
} from 'shell-models-lib';
import { switchMap, share, distinctUntilChanged } from 'rxjs/operators';
import { DataFlow } from '../dataflow/dataflow';
import { DataFlowCleanupService } from 'app/services/dataflow-cleanup.service';
import { ExecuteDataFlowActivity } from 'app/model/pipeline/execute-dataflow.activity';
import { BaseExtensiveValidationService } from '../services/base-extensive-validation.service';
import { ExecuteFlowletTransformation } from '../dataflow/executeflowlettransformation';
import { SqlPoolTableDataset } from '../dataset/sql-pool-table.dataset';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { isMap, isSet } from 'lodash';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { GuardFeatures } from 'app/services/exposure-control/ec.typings';
import { logFabricExperiment } from 'app/shared/utils/pipeline-copilot/copilot.util';
import { loggerConstants } from 'app/logger/logger.constants';
import { Status } from '@trident/certified-events';
import { FabricFeatureName } from 'app/logger/logger.fabric-certificied.event';

export class Validator {
    public static readonly className = 'Validator';

    private static _loggingConcepts: string[] = [
        ModelConstants.trigger,
        ModelConstants.pipeline,
        ModelConstants.dataFlow,
        ModelConstants.dataset,
        ModelConstants.linkedService
    ];

    // eslint-disable-next-line brace-style
    private constructor() {}

    /**
     * Validates a set of models.
     * @param deDupe Set to true to avoid error duplicates in case the passed models were part of a model heirarchy. Set
     *               to false to include all errors (useful when you validate a list of siblings and know that there won't be dupes such
     *               as in the case of parameters, we want to get parameter 1 and parameter N errors and not only parameter 1).
     * @param owneds The models to validate.
     */
    public static async validate(
        deDupe: boolean,
        context: IValidationContext,
        ...owneds: Owned[]
    ): Promise<IOwnedViolation[] | undefined> {
        return this._validate(deDupe, { ...context, isPublish: false }, ...owneds);
    }

    public static async validatePublish(
        deDupe: boolean,
        context: IValidationContext,
        ...owneds: Owned[]
    ): Promise<IOwnedViolation[] | undefined> {
        return this._validate(deDupe, { ...context, isPublish: true }, ...owneds);
    }

    public static async _validate(
        deDupe: boolean,
        context: IValidationContext,
        ...owneds: Owned[]
    ): Promise<IOwnedViolation[] | undefined> {
        // For every modelled object you encounter, find all the attributes and their
        // values. Validate it according to the following rule
        // Find all attribute constraints and validate.
        const visitedConceptObjects = new Set<Named>();
        const entitiesWithViolations = new Set<Owned>();
        const encounteredModels = new Map<IModel, Named>();
        const violations: IOwnedViolation[] = [];
        const dataFlowCleanUpService = await getServiceInstance<DataFlowCleanupService>(
            context.contextData,
            'DataFlowCleanupService'
        );
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');

        let lastSleepTime = performance.now();
        for (let i = 0; i < owneds.length; i++) {
            const ownedViolations: IOwnedViolation[] = [];
            const owned = owneds[i];
            const ownedModel = Metamodel.modelOf(owned);
            const conceptName = ownedModel?.concept?.name;
            if (conceptName && Validator._loggingConcepts.includes(conceptName)) {
                logger.log(Validator.className, 'Start validation for: ' + conceptName + ' - ' + owned.named?.name);
            }
            try {
                if (__IS_A365__ && ownedModel?.concept && owned.named) {
                    const model = ownedModel.concept;
                    const converted = owned.named;

                    // Choose whichever named changed
                    const named =
                        encounteredModels.get(model) == null ||
                        ModelMarker.isDirty(converted) ||
                        ModelMarker.isNew(converted)
                            ? converted
                            : encounteredModels.get(model);
                    encounteredModels.set(model, named);
                }

                if (deDupe && visitedConceptObjects.has(owned.concept as Named)) {
                    if (owned instanceof DataFlow) {
                        if (context.isPublish) {
                            dataFlowCleanUpService.addDataFlowToCleanUp(owned);
                        } else {
                            dataFlowCleanUpService.cleanUpDataFlow(owned, entitiesWithViolations);
                        }
                    }
                    continue;
                }
                if (deDupe) {
                    visitedConceptObjects.add(owned.concept as Named);
                }

                // When there are lots of owneds to be validate, this method would take long time and block UI (e.g. validateAll in large factory)
                // For validation with 1000ms time window or 100 items, break and move to end of execution queue to make UI relatively responsive
                // Better solution is to move CPU intensive logic to web worker so that main thread won't be blocked, tracked by work item 417541
                const now = performance.now();
                if (i % 100 === 0 || now - lastSleepTime >= 1000) {
                    lastSleepTime = now;
                    await sleep(0);
                }

                /*
                 * Here we pass the owned as the top level ancestor because it will help us know the top parent
                 * of all the inner nameds within the tree. This is particularely useful for expression validation
                 * where an activity references a dataset which has parameters and the dataset is trying to reference
                 * the pipeline's parameters. The expression resolver requires this top level ancestor to be able to
                 * resolver the expression properly.
                 */
                await Validator._validateInternal(
                    owned,
                    owned,
                    visitedConceptObjects,
                    ownedViolations,
                    owned,
                    context,
                    entitiesWithViolations
                );

                if (owned instanceof DataFlow && ownedViolations.length <= 0) {
                    if (context.isPublish) {
                        dataFlowCleanUpService.addDataFlowToCleanUp(owned);
                    } else {
                        dataFlowCleanUpService.cleanUpDataFlow(owned);
                    }
                }
                if (!context.isPublish) {
                    dataFlowCleanUpService.cleanUpStoredDataFlows();
                }
                violations.push(...ownedViolations);
            } catch (e) {
                logger.error(
                    Validator.className,
                    `Unhandled validation error for: ${conceptName} - ${
                        owned.named?.name
                    }, error: ${convertObjectToString(e)}`
                );
            }
        }

        if (__IS_A365__ && !GlobalVariables.isCmdApiApp) {
            const extensiveA365Validation = await getServiceInstance<BaseExtensiveValidationService>(
                context.contextData,
                'BaseExtensiveValidationService'
            );
            const extensiveViolations = await extensiveA365Validation.validateRbac(context, encounteredModels);
            violations.push(...extensiveViolations);
        }
        if (context.validateConnectionPermissionOnly) {
            return violations.filter((item) => item.isConnectionPermissionError);
        }
        return violations;
    }

    /**
     * Non-expression validation helper function on a field. Currently used for setting error count displayed on tab title
     * @param attribute The field to validate.
     * @param entity The entity which contains the attribute.
     */
    // Use constriant on attribute to validate attribute value
    public static async getStandaloneInvalidNoneExpressionValue(
        attribute: IAttribute,
        entity: Owned,
        context: IValidationContext
    ): Promise<IViolation[]> {
        // there is a constraint on the attribute
        if (!attribute || !attribute.constraint) {
            return undefined;
        }

        try {
            // first use validator in constraint to validate
            if (attribute.constraint.validator) {
                const validatorsError: IViolation[] =
                    (await attribute.constraint.validator.apply(entity, [
                        entity,
                        attribute.value(entity),
                        undefined,
                        context
                    ])) || [];
                return validatorsError;
            }

            // second use other number/pattern/required/unique constraint to validate
            return Promise.all(
                Validators.validatorsFor(attribute.constraint).map(async (attributeValidator) => {
                    return (
                        (await attributeValidator.validate(
                            entity,
                            attribute,
                            attribute.value(entity),
                            entity,
                            context
                        )) || []
                    );
                })
            )
                .then((list) => list.reduce((accu, curr) => (accu || []).concat(curr)))
                .catch(() => undefined); // ignore validation error when error happens
        } catch (error) {
            return undefined;
        }
    }

    public static traverseValue(
        modelAttributeContext: { inlineDataset?: boolean },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any,
        visitedConceptObjects: Set<Named>,
        attribute?: IAttribute
    ): boolean {
        if (!Metamodel.isModelOf(value)) {
            return false;
        }
        const attributeModel = Metamodel.modelOf(value);

        if (attribute?.isReferenced(modelAttributeContext)) {
            if (!attributeModel.concept) {
                // For now we don't deal with references that transcend concept objects
                return false;
            }

            // now only linkedservice and integration runtime specifies this property
            // when a linkedservice/integration runtime is referenced by another entity, if tag('noValidationWhenReferenced') === true, skip this linkedservice validation.
            const tagInfo = attributeModel.tag('noValidationWhenReferenced');
            if (
                tagInfo &&
                ((tagInfo instanceof Function && tagInfo.apply(this)) || (typeof tagInfo === 'boolean' && !!tagInfo))
            ) {
                return false;
            }

            // Else add it to the list that we are traversing
            if (visitedConceptObjects.has(value as Named)) {
                return false;
            }
            visitedConceptObjects.add(value as Named);
        }
        return true;
    }

    private static async _getAttributeViolations(
        attribute: IAttribute,
        obj: ValidateInternalObject,
        context: IValidationContext
    ): Promise<IOwnedViolation[]> {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        const aObject = obj.aObject;
        const model = obj.model;
        const object = obj.object;
        const owned = obj.owned;
        const violations: IOwnedViolation[] = [];
        // there is a constraint on the attribute and the attribute is NOT parameterized
        const attributeValidators = Validators.validatorsFor(attribute.constraint);
        for (const attributeValidator of attributeValidators) {
            if (
                model === ExecuteSSISPackageActivity.MODEL &&
                attribute === ExecuteSSISPackageActivity.PACKAGE_PATH &&
                attributeValidator === RequiredValidator.INSTANCE
            ) {
                // Ssis package path is conditional requried. It will be validated by its own validator
                continue;
            }

            if (
                __IS_A365__ &&
                GlobalVariables.isCmdApiApp &&
                model === SqlPoolTableDataset.MODEL &&
                attribute.name === SqlPool.MODEL.name &&
                attributeValidator === RequiredValidator.INSTANCE
            ) {
                // Skip Synapse dedicated SQL pool validation for dataset in Git mode
                continue;
            }

            const attrViolations =
                (await attributeValidator.validate(object, attribute, aObject[attribute.name], owned, context)) || [];
            for (const attrViolation of attrViolations) {
                // owned is a nested concept (such as in foreach and until pipelines), we want to use the parent instead since these are implementation details
                const shouldUseOwner = Metamodel.concepts().includes(Metamodel.modelOf(owned)) && owned.owner;
                const ownedOverride = shouldUseOwner ? owned.owner : owned;

                let customDisplayName;
                const resourceType = Metamodel.modelOf(owned).name as ResourceType;
                if (subResourceTypeMap['database'].includes(resourceType)) {
                    customDisplayName = (owned as Table).parentInfo.resourceName;
                } else if (
                    (ownedOverride instanceof Dataset || ownedOverride instanceof LinkedService) &&
                    ownedOverride.owner?.named &&
                    context.inlineDataset
                ) {
                    customDisplayName =
                        Metamodel.modelOf(ownedOverride.owner.named).name === ModelConstants.copyJobActivity
                            ? ownedOverride.owner.named.owner?.named?.name
                            : ownedOverride.owner.named.name;
                }

                if (!attrViolation.revalidate) {
                    logger.log(Validator.className, attrViolation.message(), attrViolation.logDetails);
                }

                // Mask this as a owner violation
                violations.push({
                    owned: ownedOverride,
                    context: attrViolation.context,
                    redirectTo: attrViolation.redirectTo,
                    entity: attrViolation.entity,
                    message: attrViolation.message,
                    messageType: attrViolation.messageType,
                    logDetails: attrViolation.logDetails,
                    attribute,
                    customDisplayName,
                    isConnectionPermissionError: attrViolation.isConnectionPermissionError,
                    corrected$: !attrViolation.doNotAutomaticallyCorrect
                        ? interval(500).pipe(
                              switchMap(async () => {
                                  const validateResult = await attributeValidator.validate(
                                      object,
                                      attribute,
                                      aObject[attribute.name],
                                      owned,
                                      context
                                  );
                                  return validateResult === undefined || validateResult.length === 0;
                              }),
                              share(),
                              distinctUntilChanged()
                          )
                        : of(null)
                });
            }
        }
        return violations;
    }

    private static async _getConstraintViolations(
        attribute: IAttribute,
        obj: ValidateInternalObject,
        context: IValidationContext
    ): Promise<IOwnedViolation[]> {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        if (!attribute.constraint.validator) {
            return [];
        }
        const aObject = obj.aObject;
        const object = obj.object;
        const owned = obj.owned;
        const violations: IOwnedViolation[] = [];
        let attrViolations = [];

        // owned is a nested concept (such as in foreach and until pipelines), we want to use the parent instead since these are implementation details
        const ownedOverride =
            Metamodel.concepts().includes(Metamodel.modelOf(owned)) && owned.owner ? owned.owner : owned;
        const ecSvc = await getServiceInstance<ExposureControlService>(context.contextData, 'ExposureControlService');
        if (!ShellGlobalVariables.isCmdApiApp && ecSvc.enableLargeActivityLimit && ownedOverride instanceof Pipeline) {
            ShellGlobalVariables.maxActivityLimit = ecSvc.getCombinedFeatureValue(
                GuardFeatures.ADFMaxActivityCountWithLargeNumberOfActivities,
                ShellGlobalVariables.maxActivityLimit,
                (response) => (typeof response === 'string' ? Number.parseInt(response, 10) : undefined)
            );
        }
        attrViolations =
            (await attribute.constraint.validator.apply(object, [
                owned,
                aObject[attribute.name],
                undefined,
                context
            ])) || [];
        for (const violation of attrViolations) {
            const violationModel = violation as IViolation;

            let customDisplayName;
            const resourceType = Metamodel.modelOf(owned).name as ResourceType;
            if (subResourceTypeMap['database'].includes(resourceType)) {
                customDisplayName = (owned as Table).parentInfo.resourceName;
            } else if (
                (ownedOverride instanceof Dataset || ownedOverride instanceof LinkedService) &&
                ownedOverride.owner?.named &&
                context.inlineDataset
            ) {
                customDisplayName =
                    Metamodel.modelOf(ownedOverride.owner.named).name === ModelConstants.copyJobActivity
                        ? ownedOverride.owner.named.owner?.named?.name
                        : ownedOverride.owner.named.name;
            }

            if (!violation.revalidate) {
                logger.log(Validator.className, violation.message(), violation.logDetails);
            }

            // Mask this as a owner violation
            violations.push({
                owned: ownedOverride,
                context: violation.context,
                redirectTo: violation.redirectTo,
                entity: violation.entity,
                message: violation.message,
                messageType: violation.messageType,
                logDetails: violation.logDetails,
                attribute,
                customDisplayName,
                isConnectionPermissionError: violation.isConnectionPermissionError,
                corrected$: !violation.doNotAutomaticallyCorrect
                    ? interval(500).pipe(
                          switchMap(async () => {
                              if (violationModel && violationModel.revalidate) {
                                  return violationModel.revalidate();
                              }

                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              const validateResult = await attribute.constraint!.validator!.apply(object, [
                                  owned,
                                  aObject[attribute.name],
                                  violation,
                                  { ...context, isRetryStage: true }
                              ]);
                              return validateResult === undefined || validateResult.length === 0;
                          }),
                          share(),
                          distinctUntilChanged()
                      )
                    : of(null)
            });
        }
        return violations;
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private static async _getNoneExpressionViolations(
        attribute: IAttribute,
        obj: ValidateInternalObject,
        context: IValidationContext
    ): Promise<IOwnedViolation[]> {
        return [
            ...(await Validator._getAttributeViolations(attribute, obj, context)),
            ...(await Validator._getConstraintViolations(attribute, obj, context))
        ];
    }

    private static async _getExpressionViolations(
        attribute: IAttribute,
        obj: ValidateInternalObject,
        context: IValidationContext
    ): Promise<IOwnedViolation[]> {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        const expressionResolverService = await getServiceInstance<ExpressionResolverService>(
            context.contextData,
            'ExpressionResolverService'
        );
        const expression = attribute.templateValue(obj.object);
        let requiredOwner = obj.ancestor;

        // To validate the attributes correctly, the Owned object needs to be used to
        // instead of the ancestor in order to use the correct validators.
        if (
            obj.owned instanceof Dataset ||
            obj.owned instanceof Pipeline ||
            obj.owned instanceof Activity ||
            obj.owned instanceof LinkedService ||
            obj.owned instanceof Trigger
        ) {
            requiredOwner = obj.owned;
        }

        let owned = obj.owned;

        // Azure Key Vault model are located under LinkedServices.
        if (obj.object instanceof AzureKeyVault) {
            owned = requiredOwner;
        }

        const resolution = expressionResolverService.given(requiredOwner, attribute).resolve(expression);
        const isResolutionError = resolution && !resolution.isValid && !resolution.treatAsWarning;
        const violations = [];
        if (isResolutionError) {
            const expressionErrorMessage = buildExpressionErrorMessage(resolution.error);
            const resolutionError = resolution.error
                ? `, resolution.error: '${convertObjectToString(resolution.error)}'`
                : '';

            logger.log(
                Validator.className,
                `Error for Expression: '${expression}', ` +
                    `Expression Error Message: '${expressionErrorMessage}', ` +
                    `attribute: '${convertObjectToString(attribute)}', ` +
                    `obj: '${convertObjectToString(obj)}', ` +
                    `resolution: '${convertObjectToString(resolution)}'` +
                    resolutionError
            );
            logFabricExperiment({
                logger,
                properties: {
                    featureName: FabricFeatureName.Pipeline,
                    operationName: loggerConstants.ExpressionBuilder_ExpressionError,
                    status: Status.Failed,
                    activityAttributes: {
                        errorKind: resolution.error?.kind
                    }
                }
            });

            violations.push({
                owned,
                attribute,
                corrected$: interval(500).pipe(
                    switchMap(async () => {
                        const expression = attribute.templateValue(obj.object);
                        if (expression == null) {
                            return true;
                        }
                        const resol = expressionResolverService.given(requiredOwner, attribute).resolve(expression);
                        return !resol || resol.isValid || resol.treatAsWarning;
                    }),
                    share(),
                    distinctUntilChanged()
                ),
                message: () => expressionErrorMessage,
                logDetails: {
                    functionName: 'Validator._getExpressionViolations',
                    errorType: 'Other' as ValidationErrorType
                }
            });
        }
        return violations;
    }

    private static async _validateAttribute(
        attribute: IAttribute,
        obj: ValidateInternalObject,
        violations: IOwnedViolation[],
        context: IValidationContext
    ): Promise<void> {
        const attributeIsNotExpression = attribute.constraint && attribute.templateValue(obj.object) === undefined;
        const attributeIsExpression = attribute.templateValue(obj.object) !== undefined;
        if (attributeIsNotExpression) {
            violations.push(...(await Validator._getNoneExpressionViolations(attribute, obj, context)));
        } else if (attributeIsExpression) {
            const ecSvc = await getServiceInstance<ExposureControlService>(
                context.contextData,
                'ExposureControlService'
            );
            const isValidationEnabled =
                ecSvc.getCombinedFeatureValue(GuardFeatures.ExpressionValidation, __IS_FABRIC_DPE__) &&
                GlobalVariables.expressionValidation;
            if (
                isValidationEnabled &&
                (!attribute.bypassExpressionValidation || !attribute.bypassExpressionValidation(obj.object as Owned))
            ) {
                const foundViolations = await Validator._getExpressionViolations(attribute, obj, context);
                violations.push(...foundViolations);
            }
        }
    }

    private static async _getAttributesViolations(
        obj: ValidateInternalObject,
        violations: IOwnedViolation[],
        context: IValidationContext
    ): Promise<void> {
        for (const attribute of obj.model.attributes) {
            await this._validateAttribute(attribute, obj, violations, context);
        }
    }

    private static async _getModelViolations(
        obj: ValidateInternalObject,
        context: IValidationContext
    ): Promise<IOwnedViolation[]> {
        const logger = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        const model = obj.model;
        const object = obj.object;
        const owned = obj.owned;
        const allModelViolations: IOwnedViolation[] = [];
        const appliedModelViolations = model.validator ? await model.validator.call(object, owned, context) : [];
        const modelViolations = appliedModelViolations || [];

        for (const violation of modelViolations) {
            const violationModel = violation as IViolation;

            let customDisplayName;
            const resourceType = Metamodel.modelOf(owned).name as ResourceType;
            if (subResourceTypeMap['database'].includes(resourceType)) {
                customDisplayName = (owned as Table).parentInfo.resourceName;
            } else if (
                (owned instanceof Dataset || owned instanceof LinkedService) &&
                owned.owner?.named &&
                context.inlineDataset
            ) {
                customDisplayName =
                    Metamodel.modelOf(owned.owner.named).name === ModelConstants.copyJobActivity
                        ? owned.owner.named.owner?.named?.name
                        : owned.owner.named.name;
            }

            if (!violation.revalidate) {
                logger.log(Validator.className, violation.message(), violation.logDetails);
            }

            // Mask this as a owner violation
            allModelViolations.push({
                owned,
                context: violation.context,
                redirectTo: violation.redirectTo,
                entity: violation.entity,
                message: violation.message,
                messageType: violation.messageType,
                logDetails: violation.logDetails,
                attribute: model,
                customDisplayName,
                isConnectionPermissionError: violation.isConnectionPermissionError,
                corrected$: !violation.doNotAutomaticallyCorrect
                    ? interval(500).pipe(
                          switchMap(async () => {
                              if (violationModel && violationModel.revalidate) {
                                  return violationModel.revalidate();
                              }

                              const validateResult = model.validator
                                  ? await model.validator.call(object, owned, context)
                                  : [];
                              return validateResult === undefined || validateResult.length === 0;
                          }),
                          distinctUntilChanged()
                      )
                    : of(null)
            });
        }
        return allModelViolations;
    }

    private static _doValidate(model: IModel): boolean {
        // We don't validate object with noValidation is true or resolved into true
        // Currently only linkedservice model sets this property as a function resolved into a boolean based on feature flag.
        const tagInfo = model.tag('noValidation');
        return !(
            tagInfo &&
            ((tagInfo instanceof Function && tagInfo.apply(this)) || (typeof tagInfo === 'boolean' && !!tagInfo))
        );
    }

    private static _isDisabledActivity(obj: ValidateInternalObject): boolean {
        return obj.owned instanceof Activity && obj.owned.state === 'Inactive';
    }

    private static async _validateProperties(
        obj: ValidateInternalObject,
        visitedConceptObjects: Set<Named>,
        violations: IOwnedViolation[],
        context: IValidationContext,
        entitiesWithViolations: Set<Owned>
    ): Promise<void> {
        const model = obj.model;
        const object = obj.object;
        const owned = obj.owned;
        const propertyInspector = DefaultPropertyInspector.instance;
        let ancestor = obj.ancestor;

        // In order to validate the properties of a model, the validation process needs
        // to occur under the appropriate context. The ancestor is reset in order for
        // attributes evaluated down the tree to have access to the correct information
        // (parameters,output, etc.)
        if (
            obj.owned instanceof Dataset ||
            obj.owned instanceof Pipeline ||
            obj.owned instanceof Activity ||
            obj.owned instanceof LinkedService ||
            obj.owned instanceof Trigger
        ) {
            ancestor = obj.owned;
        }

        // Perform a traversal and validate each modelled object you encounter.
        // We use the simple property inspector since we don't traverse listed objects
        for (const property of propertyInspector.getPropertyNames(object)) {
            const propertyValue = propertyInspector.getPropertyValue(object, property);
            if (!propertyValue) {
                // Should work for any falsy value including nulls or 0s
                continue;
            }

            const attribute = model.hasAttribute(property) ? model.attribute(property) : undefined;
            // We are just trying to cover everthing
            if (attribute && attribute.transient) {
                continue;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const values: any = [];
            // We add this special handling for map so we can validate the porperties withing each key of the map and apply the same checks for array/set for each of them
            if (isMap(propertyValue)) {
                for (const key of propertyValue.keys()) {
                    values.push(propertyValue.get(key));
                }
            } else {
                values.push(propertyValue);
            }
            for (const value of values) {
                if (Array.isArray(value)) {
                    // eslint-disable-next-line @typescript-eslint/prefer-for-of
                    for (let index = 0; index < value.length; index++) {
                        // Force to skip validation for undefined value in array.
                        if (value[index] === undefined && attribute && attribute.ignoreUndefinedInArray) {
                            continue;
                        }

                        if (
                            Validator.traverseValue(
                                { inlineDataset: context.inlineDataset },
                                value[index],
                                visitedConceptObjects,
                                attribute
                            )
                        ) {
                            await Validator._validateInternal(
                                value[index],
                                owned,
                                visitedConceptObjects,
                                violations,
                                ancestor,
                                context,
                                entitiesWithViolations
                            );
                        }
                    }
                } else if (isSet(value)) {
                    for (const item of value) {
                        if (
                            Validator.traverseValue(
                                { inlineDataset: context.inlineDataset },
                                item,
                                visitedConceptObjects,
                                attribute
                            )
                        ) {
                            await Validator._validateInternal(
                                item,
                                owned,
                                visitedConceptObjects,
                                violations,
                                ancestor,
                                context,
                                entitiesWithViolations
                            );
                        }
                    }
                } else {
                    if (
                        Validator.traverseValue(
                            { inlineDataset: context.inlineDataset },
                            value,
                            visitedConceptObjects,
                            attribute
                        )
                    ) {
                        await Validator._validateInternal(
                            value,
                            owned,
                            visitedConceptObjects,
                            violations,
                            ancestor,
                            context,
                            entitiesWithViolations
                        );
                    }
                }
            }
        }
    }

    /**
     * @param ancestor is the top level owner of the owned, which could be the owned itself.
     */
    private static async _validateInternal(
        object: {},
        owned: Owned,
        visitedConceptObjects: Set<Named>,
        violations: IOwnedViolation[],
        ancestor: Owned,
        context: IValidationContext,
        entitiesWithViolations: Set<Owned>
    ): Promise<void> {
        if (object instanceof ExecuteDataFlowActivity && object.dataflow && !object.dataflow.hasParsed) {
            await object.dataflow.setupCache(context.contextData);
            const dataFlowCleanUpService = await getServiceInstance<DataFlowCleanupService>(
                context.contextData,
                'DataFlowCleanupService'
            );
            dataFlowCleanUpService.addDataFlowToCleanUp(object.dataflow);
        }
        if (object instanceof ExecuteFlowletTransformation && object.flowlet && !object.flowlet.hasParsed) {
            await object.flowlet.setupCache(context.contextData);
            const dataFlowCleanUpService = await getServiceInstance<DataFlowCleanupService>(
                context.contextData,
                'DataFlowCleanupService'
            );
            dataFlowCleanUpService.addDataFlowToCleanUp(object.flowlet);
        }
        const obj = new ValidateInternalObject(object, owned, ancestor);
        if (!Validator._doValidate(obj.model) || Validator._isDisabledActivity(obj)) {
            // Skip validation for disabled activities
            return;
        }

        // Make sure A365 objects have synchronized their latest UI states to models.
        if (__IS_A365__ && !GlobalVariables.isCmdApiApp) {
            const extensiveA365Validation = await getServiceInstance<BaseExtensiveValidationService>(
                context.contextData,
                'BaseExtensiveValidationService'
            );
            await extensiveA365Validation.ensureA365ObjectState(obj);
        }

        await Validator._getAttributesViolations(obj, violations, context);
        const modelViolations = await Validator._getModelViolations(obj, context);
        violations.push(...modelViolations);

        // Database doesn't need properties validations as tables and relationships will be marked as dirty if they need validation
        // If we do this validation, there will be duplicate validation results, and also some unwanted validations
        if (!(obj.object instanceof Database)) {
            await Validator._validateProperties(
                obj,
                visitedConceptObjects,
                violations,
                context,
                entitiesWithViolations
            );
        }
        if (violations.length) {
            entitiesWithViolations.add(ancestor);
        }
    }
}
